import logger from '../logger';
import SB from '../SchemaBuilder';

const baseJobSchema = SB.object({
    createdAt: SB.int(),
    consumedCount: SB.int(),
});

const sendTelegramChatbotMessageJobSchema = SB.object({
    ...baseJobSchema.getFullSchema(),
    type: SB.tuple(['sendTelegramChatbotMessage' as const]),
    analyticsUserProperties: SB.map(SB.unknown()).optional(),
    chatId: SB.union([SB.int(), SB.string()]).optional(),
    maxDelayMs: SB.int(),
    message: SB.map(SB.unknown()),
    payload: SB.map(SB.unknown()),
    pinned: SB.boolean().optional(),
    priority: SB.tuple(['high'] as const).optional(),
    receiverId: SB.string(),
    replyToMessageId: SB.int().optional(),
    templateName: SB.string(),
});

const backgroundJobSchema = SB.union([sendTelegramChatbotMessageJobSchema]);

type BaseJob = SB.ExtractType<typeof baseJobSchema>;

export type SendTelegramChatbotMessageJob = SB.ExtractType<typeof sendTelegramChatbotMessageJobSchema>;

export type BackgroundJob = SB.ExtractType<typeof backgroundJobSchema>;

type JobArgs<T extends BackgroundJob> = Omit<T, 'type' | keyof BaseJob>;

const logSchema = SB.object({ logType: SB.tuple(['bgJob' as const]), job: SB.string() });

/**
 * Methods for initiating various background jobs.
 *
 * A background job is written to console logs, picked up by a CloudWatch subscription filter, pushed to a Kinesis stream and finally processed by an ECS service.
 *
 * @see https://playco.atlassian.net/wiki/spaces/CTD/pages/2517729281/Telegram+chatbot+message+queue
 */
export abstract class BackgroundJobs {
    /** @returns `true` if background jobs are enabled. */
    static isEnabled(): boolean {
        return process.env.ENABLE_BACKGROUND_JOBS === 'true';
    }

    private static logBackgroundJob(args: Omit<BackgroundJob, keyof BaseJob>): void {
        if (!this.isEnabled()) {
            throw Error('Background jobs are not enabled. Deploy the job runner infra to enable background jobs.');
        }

        const baseArgs: BaseJob = { consumedCount: 0, createdAt: Date.now() };

        const job: BackgroundJob = { ...baseArgs, ...args };

        const logMessage: SB.ExtractType<typeof logSchema> = {
            logType: 'bgJob', // A CloudWatch subscription filter provisioned in https://github.com/play-co/replicant-games matches against this property
            job: JSON.stringify(job),
        };

        logger.log(logMessage);
    }

    static sendTelegramChatbotMessage(args: JobArgs<SendTelegramChatbotMessageJob>): void {
        this.logBackgroundJob({ ...args, type: 'sendTelegramChatbotMessage' });
    }

    static parseBackgroundJobLog(logMessage: string): BackgroundJob | void {
        try {
            const rawJob = JSON.parse(logMessage.substring(logMessage.indexOf('{')));

            if (!logSchema.isValid(rawJob)) {
                return;
            }

            const job = JSON.parse(rawJob.job);

            if (backgroundJobSchema.isValid(job)) {
                return job;
            }
        } catch {
            //
        }
    }
}
